import { Form } from "@components";
import Hooks from "@hooks";
import { Box, Grid } from "@material-ui/core";
import React from "react";
import { Actions, Section } from "../../../../components";
import { useRequestContext } from "../../../../context";
import { DisclaimerModal } from "./disclaimer-modal";
import { messages } from "./messages";

export const SelectTarget: React.FC = () => {
  const [requestContextState, requestContextDispatch] = useRequestContext();
  const [current_user] = Hooks.useUser();
  const [user_id, error, fetching] = Hooks.useUserID(current_user?.email!);
  const prev = {
    fetching: Hooks.usePrevious(fetching),
  };
  const form = Form.useManager<{
    target: "SELF" | "MULTIPLE";
    id: string;
    name: string;
    email: string;
  }>({
    target:
      !requestContextState.values.users.length ||
      (requestContextState.values.users.length === 1 &&
        requestContextState.values.users[0].email === current_user?.email)
        ? "SELF"
        : "MULTIPLE",
    ...(requestContextState.values.users.length === 1 &&
    requestContextState.values.users[0].email === current_user?.email
      ? requestContextState.values.users[0]
      : {
          id: user_id || "",
          name: current_user?.name || "",
          email: current_user?.email || "",
        }),
  });

  // Effects

  React.useEffect(() => {
    if (prev.fetching && !fetching) {
      if (user_id && !form.values.id) {
        form.set(Form.target.VALUE, ["id", user_id]);
      }
      if (error && !form.values.id) {
        form.set(Form.target.ERROR, ["id", error]);
        requestContextDispatch({
          type: "set-message",
          message: messages.user_not_found,
          error: new Error(),
        });
      }
    }
  }, [user_id, error, fetching, prev.fetching, form, requestContextDispatch]);

  // [TODO] Update message on target change

  // Handlers

  function handleTargetValueChange(name: "SELF" | "MULTIPLE"): void {
    console.log("this value changed", name);
    requestContextDispatch({
      type: "set-message",
      message: messages.multiple_users_request,
    });
    requestContextDispatch({ type: "set-target", value: name });
    form.set(Form.target.VALUE, ["target", name]);
  }

  function handleUserIDValueChange(name: "id", value: string): void {
    form.set(Form.target.VALUE, [name, value]);
    form.set(Form.target.ERROR, [name, Form.Validators.hasValue(value)]);
  }

  function handlePreviousButtonClick(): void {
    requestContextDispatch({
      type: "set-step",
      name: "type",
      view: "select-type",
    });
  }

  function handleNextButtonClick(): void {
    if (form.values.target === "SELF") {
      requestContextDispatch({
        type: "set-users",
        users: [
          {
            id: form.values.id,
            name: form.values.name,
            email: form.values.email,
          },
        ],
      });
      requestContextDispatch({
        type: "set-step",
        name: "system-details",
        view: "select-systems",
      });
    } else {
      requestContextDispatch({
        type: "set-step",
        name: "user-information",
        view: "users-list",
      });
    }
  }

  // Helpers

  function isSectionActive(): boolean {
    return (
      requestContextState.step.name === "user-information" &&
      requestContextState.step.view === "select-target"
    );
  }

  function isUserIDTextboxDisabled(): boolean {
    return form.values.target === "SELF"
      ? form.values.id
        ? false
        : fetching
      : true;
  }

  function getUserIDTextboxError(): Error | undefined | null {
    return form.values.target === "SELF" ? form.errors.id : null;
  }

  function isUserIDTextboxLoading(): boolean {
    return form.values.target === "SELF"
      ? form.values.id
        ? false
        : fetching
      : false;
  }

  function isNextButtonDisabled(): boolean {
    return form.values.target === "SELF" && !form.values.id;
  }

  return (
    <Section active={isSectionActive()}>
      <Grid container spacing={2} component={Box} flexGrow={1}>
        <Grid item xs={12}>
          <Form>
            <Form.Radiobutton
              label="I am requesting for myself"
              name="SELF"
              onChange={handleTargetValueChange}
              size={12}
              value={form.values.target === "SELF"}
            />
            <Form.Textbox
              disabled
              label="Email"
              name="email"
              size={6}
              value={form.values.email}
            />
            <Form.Textbox
              disabled
              label="Name"
              name="name"
              size={6}
              value={form.values.name}
            />
            <Form.Textbox
              disabled={isUserIDTextboxDisabled()}
              error={getUserIDTextboxError()}
              label="Employee ID"
              loading={isUserIDTextboxLoading()}
              name="id"
              onChange={handleUserIDValueChange}
              size={6}
              value={form.values.id}
            />
            <Form.Radiobutton
              label="I am people leader / I.T Support / Service Desk Representative / HR Personnel requesting for someone else's access"
              name="MULTIPLE"
              onChange={handleTargetValueChange}
              size={12}
              value={form.values.target === "MULTIPLE"}
            />
          </Form>
        </Grid>
      </Grid>
      <Grid container spacing={2} component={Box} pt={2}>
        <Grid item xs={12}>
          <Actions>
            <Actions.Cancel />
            <Actions.Previous onClick={handlePreviousButtonClick} />
            <Actions.Next
              disabled={isNextButtonDisabled()}
              onClick={handleNextButtonClick}
            />
          </Actions>
        </Grid>
      </Grid>
      <DisclaimerModal target={form.values.target} />
    </Section>
  );
};
